/**
 * Phase G+ Feb 2026 — Export-Downloads-Hub.
 * ----------------------------------------
 * Genau 4 Export-Module nebeneinander, alle gegen den neuen Payroll-Stack
 * (payroll_engine, advances, time entries, driving logs):
 *
 *   1. Lohnverrechnung — XLSX-Lohnliste (Format wie Kundenvorlage)
 *   2. Zulagen         — XLSX-Long-Form (Lohnart pro Zeile)
 *   3. Zeiterfassung   — XLSX pro Fahrer/Tag
 *   4. Lenkprotokoll   — ZIP mit einem A4-PDF pro (Fahrer × Tag)
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  FileSpreadsheet, Receipt, Clock, Truck, Loader2, Download, CheckCircle2,
  GitCompare,
} from "lucide-react";
import SnapshotDiffDialog from "@/components/SnapshotDiffDialog";

const today = new Date();
const isoToday = today.toISOString().slice(0, 10);
const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1)
  .toISOString().slice(0, 10);

const MONATE = [
  "Jänner", "Februar", "März", "April", "Mai", "Juni",
  "Juli", "August", "September", "Oktober", "November", "Dezember",
];

const TILES = [
  {
    key: "lohnverrechnung",
    title: "Lohnverrechnung",
    subtitle: "Monatliche Lohnliste · Excel",
    icon: FileSpreadsheet,
    accent: "blue",
    desc: "Vollständige Liste aller Fahrer mit Stunden, Diäten, Überstunden, Akonto, Urlaub und Brutto/Netto-Berechnung. Snapshot-Lock pro Lauf.",
    mode: "month",
  },
  {
    key: "zulagen",
    title: "Zulagen",
    subtitle: "Lohnart-Export für Lohnverrechner · Excel",
    icon: Receipt,
    accent: "emerald",
    desc: "Long-Form-Export: eine Zeile pro Mitarbeiter × Lohnart (3100/3110/3150/3000/2500/6999/7000 …). Import-fertig für BMD/Sage.",
    mode: "month",
  },
  {
    key: "zeiterfassung",
    title: "Zeiterfassung",
    subtitle: "Arbeitszeiten · Excel",
    icon: Clock,
    accent: "amber",
    desc: "Eine Zeile pro Fahrer × Tag mit Arbeit Start/Ende, Pausen, Arbeitszeit netto, gefahrene KM und Status.",
    mode: "range",
  },
  {
    key: "lenkprotokoll",
    title: "Lenkprotokoll",
    subtitle: "A4-PDFs pro Tag · ZIP",
    icon: Truck,
    accent: "rose",
    desc: "Kodlogy-konformes ZIP: ein A4-PDF pro (Fahrer × gearbeitetem Tag) mit Lenkzeiten, Pausen und Kilometern.",
    mode: "range",
  },
];

const ACCENT_CLS = {
  blue:    { icon: "text-blue-400",    btn: "bg-blue-600 hover:bg-blue-500" },
  emerald: { icon: "text-emerald-400", btn: "bg-emerald-600 hover:bg-emerald-500" },
  amber:   { icon: "text-amber-400",   btn: "bg-amber-600 hover:bg-amber-500" },
  rose:    { icon: "text-rose-400",    btn: "bg-rose-600 hover:bg-rose-500" },
};

export default function ExportDownloads() {
  return (
    <div className="space-y-6" data-testid="export-downloads-page">
      <div>
        <h1 className="font-display text-2xl font-bold text-zinc-50">Export-Downloads</h1>
        <p className="text-sm text-zinc-400 mt-1">
          Vier zentrale Exporte für Lohnverrechnung und Behörden — alle direkt aus dem neuen Payroll-System.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {TILES.map((t) => <Tile key={t.key} tile={t} />)}
      </div>
    </div>
  );
}

function Tile({ tile }) {
  const a = ACCENT_CLS[tile.accent];
  const Icon = tile.icon;

  return (
    <div
      className="border border-zinc-800 bg-zinc-900/60 rounded-lg overflow-hidden hover:border-zinc-700 transition-colors"
      data-testid={`export-tile-${tile.key}`}
    >
      <div className="p-5 flex items-start gap-4">
        <div className={`w-10 h-10 rounded-lg bg-zinc-950 flex items-center justify-center shrink-0`}>
          <Icon className={`w-5 h-5 ${a.icon}`} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-display text-base font-semibold text-zinc-100">{tile.title}</div>
          <div className="text-[11px] uppercase tracking-wider text-zinc-500 mt-0.5">{tile.subtitle}</div>
          <p className="text-xs text-zinc-400 mt-2 leading-relaxed">{tile.desc}</p>
        </div>
      </div>
      <div className="border-t border-zinc-800 bg-zinc-950/50 px-5 py-4">
        {tile.mode === "month"
          ? <MonthForm tile={tile} />
          : <RangeForm tile={tile} />}
      </div>
    </div>
  );
}

function MonthForm({ tile }) {
  const a = ACCENT_CLS[tile.accent];
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [busy, setBusy] = useState(false);
  const [diffOpen, setDiffOpen] = useState(false);

  async function generate() {
    setBusy(true);
    try {
      const url = tile.key === "lohnverrechnung"
        ? `/payroll-lohnliste/export/${year}/${month}`
        : `/payroll-lohnliste/zulagen-export/${year}/${month}`;
      const res = await api.post(url, null, { responseType: "blob" });
      const fname = parseFilename(res.headers["content-disposition"]) ||
        `${tile.key}_${year}-${String(month).padStart(2, "0")}.xlsx`;
      downloadBlob(res.data, fname);
      toast.success(`${tile.title} erstellt`);
    } catch (e) {
      const msg = await readErrorBlob(e);
      toast.error(msg || `${tile.title}: Fehler beim Erstellen`);
    } finally {
      setBusy(false);
    }
  }

  const showDiff = tile.key === "lohnverrechnung";

  return (
    <>
      <div className="flex flex-wrap items-end gap-3">
        <Field label="Jahr">
          <select value={year} onChange={(e) => setYear(parseInt(e.target.value))}
            data-testid={`${tile.key}-year`}
            className="px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100">
            {[today.getFullYear() - 1, today.getFullYear(), today.getFullYear() + 1]
              .map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </Field>
        <Field label="Monat">
          <select value={month} onChange={(e) => setMonth(parseInt(e.target.value))}
            data-testid={`${tile.key}-month`}
            className="px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100">
            {MONATE.map((n, i) => <option key={i} value={i + 1}>{n}</option>)}
          </select>
        </Field>
        <div className="ml-auto flex items-center gap-2">
          {showDiff && (
            <button
              type="button"
              onClick={() => setDiffOpen(true)}
              data-testid={`${tile.key}-diff-btn`}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm text-blue-200 bg-blue-500/10 hover:bg-blue-500/20 border border-blue-500/40 rounded font-medium"
              title="Mit letztem Snapshot vergleichen"
            >
              <GitCompare className="w-4 h-4" />
              Vergleichen
            </button>
          )}
          <button
            onClick={generate}
            disabled={busy}
            data-testid={`${tile.key}-generate-btn`}
            className={`inline-flex items-center gap-2 px-4 py-2 text-sm text-white rounded font-medium ${a.btn} disabled:opacity-50`}
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            Datei generieren
          </button>
        </div>
      </div>

      {showDiff && diffOpen && (
        <SnapshotDiffDialog
          year={year}
          month={month}
          onClose={() => setDiffOpen(false)}
        />
      )}
    </>
  );
}

function RangeForm({ tile }) {
  const a = ACCENT_CLS[tile.accent];
  const [from, setFrom] = useState(firstOfMonth);
  const [to, setTo] = useState(isoToday);
  const [busy, setBusy] = useState(false);
  const [empCount, setEmpCount] = useState(null);

  useEffect(() => {
    api.get("/employees").then(({ data }) => {
      const items = Array.isArray(data) ? data : (data.items || []);
      setEmpCount(items.filter((e) =>
        e.role === "fahrer" && e.status !== "inaktiv" && e.status !== "abgemeldet"
      ).length);
    }).catch(() => setEmpCount(null));
  }, []);

  async function generate() {
    setBusy(true);
    try {
      const { data: empData } = await api.get("/employees");
      const allItems = Array.isArray(empData) ? empData : (empData.items || []);
      const employee_ids = allItems
        .filter((e) => e.role === "fahrer" && e.status !== "inaktiv" && e.status !== "abgemeldet")
        .map((e) => e.id);

      if (employee_ids.length === 0) {
        toast.error("Keine aktiven Fahrer gefunden");
        return;
      }

      const url = tile.key === "zeiterfassung"
        ? "/exports/zeiterfassung/excel"
        : "/exports/lenkprotokoll/zip";

      const body = { employee_ids, date_from: from, date_to: to };
      const res = await api.post(url, body, { responseType: "blob" });
      const ext = tile.key === "zeiterfassung" ? "xlsx" : "zip";
      const fname = parseFilename(res.headers["content-disposition"]) ||
        `${tile.title}_${from}_${to}.${ext}`;
      downloadBlob(res.data, fname);
      toast.success(`${tile.title} erstellt`);
    } catch (e) {
      const msg = await readErrorBlob(e);
      toast.error(msg || `${tile.title}: Fehler beim Erstellen`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-3">
        <Field label="Von">
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)}
            data-testid={`${tile.key}-from`}
            className="px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100"/>
        </Field>
        <Field label="Bis">
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)}
            data-testid={`${tile.key}-to`}
            className="px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100"/>
        </Field>
        <button
          onClick={generate}
          disabled={busy}
          data-testid={`${tile.key}-generate-btn`}
          className={`ml-auto inline-flex items-center gap-2 px-4 py-2 text-sm text-white rounded font-medium ${a.btn} disabled:opacity-50`}
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
          Datei generieren
        </button>
      </div>
      {empCount !== null && (
        <div className="flex items-center gap-1.5 text-[11px] text-zinc-500">
          <CheckCircle2 className="w-3 h-3 text-emerald-500" />
          Alle <span className="text-zinc-300 font-medium">{empCount}</span> aktiven Fahrer einbezogen
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-wider text-zinc-500 block mb-1">{label}</label>
      {children}
    </div>
  );
}

function parseFilename(cd) {
  if (!cd) return null;
  const m = /filename\*?=(?:UTF-8'')?["']?([^"';]+)/i.exec(cd);
  return m ? decodeURIComponent(m[1]) : null;
}

function downloadBlob(data, fname) {
  const url = URL.createObjectURL(new Blob([data]));
  const a = document.createElement("a");
  a.href = url;
  a.download = fname;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

async function readErrorBlob(e) {
  try {
    const text = await e?.response?.data?.text?.();
    if (text) {
      const j = JSON.parse(text);
      return j?.detail || null;
    }
  } catch (_err) {
    /* swallow — fall through to detail/message extraction below */
  }
  return e?.response?.data?.detail || e?.message || null;
}
